import React, { useState, useEffect } from "react";
import PageTemplate from "../../../Templates/PageTemplate";
import middleware from "../../../Api/Middleware";
import { useNavigate, useParams } from "react-router";
import Key from "./Icons/key.svg";
import Eye from "./Icons/eye.svg";
import EyeClosed from "./Icons/eye-closed.svg";
import "./EditUsers.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faBan, faUnlockKeyhole } from "@fortawesome/free-solid-svg-icons";
import Modal from 'react-bootstrap/Modal';
import ToastSuccess from "../../../Toasts/ToastSuccess";
import ToastError from "../../../Toasts/ToastError";
import PasswordRequirementsModal from './Modals/PasswordRequirementsModal';
import SecurityCodeModal from './Modals/SecurityCodeModal';
import RoleInfoModal from './Modals/RoleInfoModal';
import ToastInfo from "../../../Toasts/ToastInfo";
import DeactivateUserModal from "./Modals/DeactivateUserModal";
import { BackButton, ButtonBlue, ButtonDarkBlue } from "../../../HouseStyle/Components/Buttons";
import { BorderedTextInput, RegularTextInput } from "../../../HouseStyle/Components/Inputs";
import { BorderedDropDown } from "../../../HouseStyle/Components/DropDowns";
import { SUPPORTED_LANGUAGE_OPTIONS } from "../../../../utils";
import ToastPromise from "../../../Toasts/ToastPromise";

const roleOptions = [
  { value: 0, label: "Alleen bestellingen" },
  { value: 1, label: "Medewerker" },
  { value: 2, label: "Leidinggevende" },
  { value: 3, label: "Manager" },
  { value: 4, label: "Admin" },
];

const EditUsers = ({ ownUserRole, ownUserId }) => {
  const navigate = useNavigate();
  const [updateUserValues, setUpdateUserValues] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showSecurityCode, setShowSecurityCode] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user_id } = useParams();
  const [modals, setModals] = useState({
    password: false,
    role: false,
    securityCode: false,
    deactivateUser: false
  });
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d])[A-Za-z\d\S]{8,}$/;

  const toggleModal = (modal) => setModals((prev) => ({ ...prev, [modal]: !prev[modal] }));


  const fetchAll = async () => {
    try {
      setLoading(true);
      const [fetchUser] = await Promise.all([
        middleware.get(`users?user_id=${user_id}`),
      ]);
      setUpdateUserValues(fetchUser.data.data)
      setLoading(false)
    } catch (error) {
      ToastError(error.message)
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const validateEmail = () => {
    if (!emailRegex.test(updateUserValues.email)) {
      ToastError(`Ongeldig e-mailadres.`, 'invalid_email', 3000);
      return false;
    } else {
      return true;
    }
  };

  const validatePassword = () => {
    if (updateUserValues.password && !passwordRegex.test(updateUserValues.password)) {
      ToastError(`Wachtwoord moet minimaal 8 tekens bevatten, inclusief hoofdletters, kleine letters, een cijfer en een speciaal teken.`, 'invalid_password');
      return false;
    } else {
      return true;
    }
  };

  const validateSecurityCode = () => {
    if (updateUserValues.security_code && (updateUserValues.security_code.length < 4 || updateUserValues.security_code.length > 4)) {
      ToastError(`Beveiligingspin moet 4 cijfers bevatten.`, 'invalid_security_code', 3000);
      return false;
    } else {
      return true;
    }
  }

  const generateRandomPassword = async (event) => {
    event.preventDefault();

    const length = 15; // Minimum length is 8, so 15 is sufficient
    const lowercase = "abcdefghijklmnopqrstuvwxyz";
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const digits = "0123456789";
    const specialChars = "!@#$%^&*";

    // Ensure each type is included at least once
    let password = [
      lowercase[Math.floor(Math.random() * lowercase.length)],
      uppercase[Math.floor(Math.random() * uppercase.length)],
      digits[Math.floor(Math.random() * digits.length)],
      specialChars[Math.floor(Math.random() * specialChars.length)],
    ];

    // Fill the rest of the password
    const allChars = lowercase + uppercase + digits + specialChars;
    while (password.length < length) {
      password.push(allChars[Math.floor(Math.random() * allChars.length)]);
    }

    // Shuffle the password to ensure randomness
    password = password.sort(() => Math.random() - 0.5).join('');

    // Update state
    setUpdateUserValues({ ...updateUserValues, password });

    try {
      await navigator.clipboard.writeText(password);
    } catch (err) {
      console.error("Failed to copy password: ", err);
    }
  };

  const generateRandomSecurityCode = async event => {
    event.preventDefault();
    const length = 4;
    const charset = "0123456789";
    let code = "";

    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      let char = charset[randomIndex];
      code += char;
    }

    setUpdateUserValues({ ...updateUserValues, security_code: code });

    try {
      await navigator.clipboard.writeText(code);
    } catch (err) {
      console.error('Failed to copy password: ', err);
    }
  }

  const updateUser = async (goBack = false) => {
    if (validateEmail() && validatePassword() && validateSecurityCode()) {
      await ToastPromise({
        promise: middleware.put('users', updateUserValues).then(() => {
          if (goBack) {
            navigate(-1);
          }
        }),
        pending: 'Gebruikergegevens bijwerken...',
        successRenderer: () => 'Gebruikergegevens succesvol bijgewerkt!',
        errorRenderer: (error) => {
          console.error(error);
          return error?.response?.data?.error || 'Er is iets onverwachts fout gegaan tijdens het bijwerken van de gebruiker.';
        },
        autoClose: 2000
      });
    }
  };

  return (
    <>
      <PasswordRequirementsModal show={modals.password} onHide={() => toggleModal("password")} />
      <SecurityCodeModal show={modals.securityCode} onHide={() => toggleModal("securityCode")} />
      <RoleInfoModal show={modals.role} onHide={() => toggleModal("role")} />
      <DeactivateUserModal show={modals.deactivateUser} onHide={() => toggleModal("deactivateUser")} updateUserValues={updateUserValues} setState={setUpdateUserValues} updateUser={updateUser} />

      <PageTemplate navbarTitle={"Gebruiker bewerken"} pageIsLoading={loading}>
        <div className="w-100">
          <div className="d-flex flex-row justify-content-between mb-3">
            <div className="d-flex">
              <div className="d-flex flex-row me-5 align-items-center">
                <BackButton onClickFunction={() => navigate(-1)} text={"Terug"} />
              </div>
              <div className="d-flex flex-column align-items-center">
                {updateUserValues.account_status === 0 ? <p className="mb-1 p-0">Geblokkeerd</p> : <p className="mb-1 p-0">Geactiveerd</p>}
                <label className="switchActiveProduct">
                  <input type="checkbox" checked={updateUserValues.account_status}
                    onChange={(e) => toggleModal("deactivateUser")} />
                  <span className="sliderActiveProduct round">
                  </span>
                </label>
              </div>
            </div>
            <div className="d-flex gap-3">
              <ButtonDarkBlue text={"Opslaan & Sluiten"} onClickFunction={(e) => updateUser(true)} />
              <ButtonBlue text={"Opslaan"} onClickFunction={(e) => updateUser()} />
            </div>
          </div>
          <div className="d-flex flex-row justify-content-between">
            <div className="whiteBox w-50 me-2 p-5">
              <p className="TableTitle ">Systeemgebruiker aanpassen</p>
              <div className="w-50 InputRow flex-column" style={{alignItems:'initial'}}>
                <BorderedTextInput placeholder='Voornaam' inputValue={updateUserValues.first_name} setInputValue={(value) => setUpdateUserValues(prev => ({ ...prev, first_name: value }))} size='l' label={"Voornaam"} />
                <BorderedTextInput placeholder='Achternaam' inputValue={updateUserValues.last_name} setInputValue={(value) => setUpdateUserValues(prev => ({ ...prev, last_name: value }))} size='l' label={"Achternaam"} />
                <BorderedTextInput type={'date'} max={new Date().toISOString().split('T')[0]} placeholder='Geboortedatum' inputValue={updateUserValues.date_of_birth} setInputValue={(value) => setUpdateUserValues(prev => ({ ...prev, date_of_birth: value }))} size='l' label={"Geboortedatum"} />

                <BorderedDropDown
                  placeholder="Selecteer een land"
                  label={"Taal"}
                  options={[
                    ...SUPPORTED_LANGUAGE_OPTIONS.map((language) => ({
                      title: language.lang,
                      value: language.code,
                      onClick: () => {
                        setUpdateUserValues({
                          ...updateUserValues,
                          language: language.value
                        });
                      }
                    }))
                  ]}
                  selectedValue={updateUserValues.language}
                  setSelectedValue={(value) => setUpdateUserValues(prev => ({ ...prev, language: value }))}
                />
                <BorderedDropDown
                  placeholder="Selecteer een rol"
                  label={
                    <div>
                      Rol
                      <FontAwesomeIcon
                        className="iconInfoStyle"
                        onClick={() => toggleModal("role")}
                        icon={faCircleInfo}
                      />
                    </div>
                  }
                  options={[
                    ...roleOptions.map((roles) => ({
                      title: roles.label,
                      value: roles.value,
                      onClick: () => {
                        setUpdateUserValues({
                          ...updateUserValues,
                          role: roles.value
                        });
                      }
                    }))
                  ]}
                  selectedValue={updateUserValues.role}
                  setSelectedValue={(e) => setUpdateUserValues(prev => ({ ...prev, role: e }))}
                />
              </div>
            </div>

            <form className="whiteBox w-50 ms-2 p-5 d-flex flex-column gap-3" autoComplete="off">
              <p className="TableTitle mb-0">Inloggegevens aanpassen</p>
              <BorderedTextInput placeholder='E-mailadres' inputValue={updateUserValues.email} setInputValue={(value) => setUpdateUserValues(prev => ({ ...prev, email: value }))} label={"E-mailadres"} />
              <div>
                <label htmlFor="change-password">
                  Wijzig wachtwoord
                  <FontAwesomeIcon
                    className="iconInfoStyle"
                    onClick={() => toggleModal("password")}
                    icon={faCircleInfo}
                  />
                </label>
                <div className="d-flex">
                  <input
                    id="change-password"
                    className={`inputFieldEditUser ps-2 w-100`}
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="new-password"
                    onChange={(event) => setUpdateUserValues(prev => ({ ...prev, password: event.target.value }))}
                    value={updateUserValues.password || ''}
                    placeholder=""
                    style={{
                      borderTopRightRadius: "0px",
                      borderBottomRightRadius: "0px",
                    }}
                  ></input>
                  <button
                    className="btn btnPasswordShow"
                    onClick={(event) => {
                      event.preventDefault();
                      setShowPassword(!showPassword);
                    }}
                  >
                    <img
                      className="h-100"
                      src={showPassword ? Eye : EyeClosed}
                    />
                  </button>
                  <button
                    className="btn btnEditUser"
                    onClick={generateRandomPassword}
                  >
                    <img className="h-100" src={Key} />
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="change-security-code">
                  Beveiligingspin instellen
                  <FontAwesomeIcon
                    className="iconInfoStyle"
                    onClick={() => toggleModal("securityCode")}
                    icon={faCircleInfo}
                  />
                </label>
                <div className="d-flex">
                  <input
                    id="change-security-code"
                    className={`inputFieldEditUser ps-2 w-100`}
                    type={showSecurityCode ? "text" : "password"}
                    name="password"
                    autoComplete="new-password"
                    onChange={(event) => setUpdateUserValues(prev => ({ ...prev, security_code: event.target.value }))}
                    value={updateUserValues.security_code || ''}
                    placeholder=""
                    style={{
                      borderTopRightRadius: "0px",
                      borderBottomRightRadius: "0px",
                    }}
                  ></input>
                  <button
                    className="btn btnPasswordShow"
                    onClick={(event) => {
                      event.preventDefault();
                      setShowSecurityCode(!showSecurityCode);
                    }}
                  >
                    <img
                      className="h-100"
                      src={showSecurityCode ? Eye : EyeClosed}
                    />
                  </button>
                  <button
                    className="btn btnEditUser"
                    onClick={generateRandomSecurityCode}
                  >
                    <img className="h-100" src={Key} />
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </PageTemplate>
    </>
  );
};

export default EditUsers;